import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ColMeta } from './faturaGridModel';
import { formatCell, editableText, parseCell } from './gridFormat';

interface Props {
  columns: ColMeta[];
  groupHeaders?: string[];
  rows: (string | number)[][];
  editable: boolean;
  edited: Set<string>;                 // chaves "r:c" (r = índice ABSOLUTO em rows)
  pendentes?: Set<string>;             // subconjunto de `edited` ainda não gravado no banco
  onCellChange: (r: number, c: number, raw: string | number) => void;
  /** Células que não aceitam edição mesmo com `editable` ligado (ex.: coluna calculada,
   *  ou um total que sai de várias parcelas e não teria onde cair). */
  celulaEditavel?: (r: number, c: number) => boolean;
  /** Ação própria da célula (abrir um detalhe, por exemplo). Quando devolve uma função,
   *  o clique executa ela em vez de começar a edição inline — e o cursor vira de clique. */
  acaoCelula?: (r: number, c: number) => (() => void) | null;
  /** Tooltip das células que têm ação — sem isto o clique fica invisível. */
  dicaAcao?: string;
  freezeCount: number;
  freezeWidths: number[];              // largura das colunas congeladas (len == freezeCount)
  statusRow?: (r: number) => 'ok' | 'revisar' | 'falhou' | undefined;
  severityCol?: number;
  highlightCol?: number;               // coluna "motivo": células não vazias ganham fundo âmbar
}

const PAGE_SIZE = 50;
const COL_MIN = 116;                    // largura mínima das colunas roláveis
const PADX = 16;                        // px-2 (8px de cada lado)

const railColor = (s?: string) =>
  s === 'ok' ? '#10b981' : s === 'revisar' ? '#f59e0b' : s === 'falhou' ? '#ef4444' : 'transparent';

const sevBg = (v: string) =>
  v === 'critico' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : v === 'alerta' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : v === 'info' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';

const VAZIO = new Set<string>();

const SpreadsheetGrid: React.FC<Props> = ({
  columns, groupHeaders, rows, editable, edited, pendentes = VAZIO, onCellChange, celulaEditavel, acaoCelula, dicaAcao, freezeCount, freezeWidths, statusRow, severityCol, highlightCol,
}) => {
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<{ r: number; c: number } | null>(null);
  const [draft, setDraft] = useState('');
  const skipBlur = useRef(false);       // evita commit duplo quando Enter/Tab/Esc move o foco

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages - 1);
  const inicio = pageSafe * PAGE_SIZE;
  const visiveis = rows.slice(inicio, inicio + PAGE_SIZE);

  const leftOf = (c: number) => freezeWidths.slice(0, c).reduce((a, b) => a + b, 0);
  const isFrozen = (c: number) => c < freezeCount;
  const lastFrozen = freezeCount - 1;
  const frozenBoxShadow = 'inset -1px 0 0 rgba(0,0,0,0.08)';

  // Estilo comum de uma célula congelada (offset + largura fixa p/ os offsets baterem).
  const frozenStyle = (c: number): React.CSSProperties =>
    isFrozen(c)
      ? { left: leftOf(c), width: freezeWidths[c], minWidth: freezeWidths[c], maxWidth: freezeWidths[c], boxShadow: c === lastFrozen ? frozenBoxShadow : undefined }
      : { minWidth: COL_MIN };

  // Conteúdo com largura pinada quando congelado (evita que nome longo desalinhe os offsets).
  const wrap = (c: number, node: React.ReactNode, extra = '') =>
    isFrozen(c)
      ? <div className={`truncate ${extra}`} style={{ width: freezeWidths[c] - PADX }}>{node}</div>
      : <>{node}</>;

  const podeEditar = (r: number, c: number) => editable && (celulaEditavel?.(r, c) ?? true);

  /** Próxima coluna que aceita edição a partir de `c` (o Tab pula as só-leitura). */
  const proximaEditavel = (r: number, c: number) => {
    for (let i = c; i < columns.length; i++) if (podeEditar(r, i)) return i;
    return -1;
  };

  const startEdit = (r: number, c: number) => {
    if (!podeEditar(r, c)) return;
    setEditing({ r, c });
    setDraft(editableText(rows[r][c], columns[c].type));
  };
  const commit = (r: number, c: number, next?: { r: number; c: number }) => {
    onCellChange(r, c, parseCell(draft, columns[c].type));
    if (next && next.r >= 0 && next.r < rows.length && next.c >= 0 && next.c < columns.length) {
      setPage(Math.floor(next.r / PAGE_SIZE));
      setEditing(next);
      setDraft(editableText(rows[next.r][next.c], columns[next.c].type));
    } else setEditing(null);
  };
  const onKey = (e: React.KeyboardEvent, r: number, c: number) => {
    if (e.key === 'Enter') { e.preventDefault(); skipBlur.current = true; commit(r, c, { r: r + 1, c }); }
    else if (e.key === 'Tab') { e.preventDefault(); skipBlur.current = true; commit(r, c, { r, c: proximaEditavel(r, c + 1) }); }
    else if (e.key === 'Escape') { e.preventDefault(); skipBlur.current = true; setEditing(null); }
  };
  const onBlur = (r: number, c: number) => {
    if (skipBlur.current) { skipBlur.current = false; return; }
    commit(r, c);
  };

  const bodyBg = (r: number) => (r % 2 === 1 ? 'bg-[#EEF0E6] dark:bg-gray-800/40' : 'bg-white dark:bg-gray-900');

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar border border-gray-200 dark:border-gray-700 rounded-xl">
        <table className="border-separate border-spacing-0 text-[12px] w-max">
          <thead>
            {groupHeaders && (
              <tr>
                {columns.map((col, c) => {
                  const bg = col.band === 'yellow' ? 'bg-[#FFFF00] text-black' : col.band === 'red' ? 'bg-[#F2DCDB] text-black' : 'bg-[#5F6C37] text-white/90';
                  return (
                    <th key={c} className={`h-7 px-2 font-semibold text-[10px] whitespace-nowrap sticky top-0 text-center ${bg} ${isFrozen(c) ? 'z-30' : 'z-20'}`} style={frozenStyle(c)}>
                      {wrap(c, groupHeaders[c] || '')}
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              {columns.map((col, c) => (
                <th
                  key={c}
                  className={`px-2 py-2 font-semibold text-white bg-[#2E3D1F] whitespace-nowrap align-middle sticky ${groupHeaders ? 'top-7' : 'top-0'} ${isFrozen(c) ? 'z-30 text-left' : 'z-20 text-center'}`}
                  style={frozenStyle(c)}
                  title={col.label}
                >
                  {wrap(c, col.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((row, i) => {
              const r = inicio + i;
              const zebra = bodyBg(r);
              const st = statusRow?.(r);
              return (
                <tr key={r}>
                  {columns.map((col, c) => {
                    const isEditing = editing?.r === r && editing?.c === c;
                    const acao = acaoCelula?.(r, c) ?? null;
                    const raw = row[c];
                    // Âmbar = alterado agora e ainda não gravado; verde = correção já salva.
                    const isEdited = edited.has(`${r}:${c}`);
                    const isPendente = pendentes.has(`${r}:${c}`);
                    const alignNum = col.type !== 'txt';
                    const isSev = c === severityCol && !!String(raw);
                    const isMotivo = c === highlightCol && String(raw) !== '';
                    const display = formatCell(raw, col.type);
                    return (
                      <td
                        key={c}
                        onClick={() => { if (isEditing) return; if (acao) acao(); else startEdit(r, c); }}
                        className={`relative px-2 py-1 whitespace-nowrap ${isMotivo ? 'bg-amber-100 dark:bg-amber-900/40' : zebra} ${alignNum ? 'text-right font-mono tabular-nums' : 'text-left'} ${isFrozen(c) ? 'sticky z-10' : ''} ${acao ? 'cursor-pointer' : podeEditar(r, c) ? 'cursor-text' : ''} ${isEdited ? (isPendente ? 'shadow-[inset_0_0_0_2px_#f59e0b]' : 'shadow-[inset_0_0_0_2px_#10b981]') : ''} border-b border-gray-100 dark:border-gray-800`}
                        data-corrigida={isEdited ? (isPendente ? 'pendente' : 'salva') : undefined}
                        style={{
                          ...frozenStyle(c),
                          ...(isFrozen(c) && isEdited ? { boxShadow: undefined } : {}),
                          ...(c === 0 && st ? { borderLeft: `3px solid ${railColor(st)}` } : {}),
                        }}
                        title={(acao && dicaAcao) || display || undefined}
                      >
                        {isEditing ? (
                          <input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => onKey(e, r, c)}
                            onBlur={() => onBlur(r, c)}
                            className={`w-full bg-white dark:bg-gray-950 outline-none ring-2 ring-emerald-500 rounded px-1 -mx-1 ${alignNum ? 'text-right font-mono' : 'text-left'}`}
                          />
                        ) : isSev ? (
                          wrap(c, <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${sevBg(String(raw))}`}>{String(raw)}</span>)
                        ) : display === '' ? (
                          wrap(c, <span className="text-gray-300 dark:text-gray-600 select-none">–</span>)
                        ) : isMotivo ? (
                          wrap(c, <span className="text-amber-900 dark:text-amber-200 font-medium">{display}</span>)
                        ) : (
                          wrap(c, <span className="text-gray-800 dark:text-gray-100">{display}</span>)
                        )}
                        {isEdited && <span className={`absolute top-0 right-0 w-1.5 h-1.5 rounded-bl ${isPendente ? 'bg-amber-500' : 'bg-emerald-500'}`} />}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rodapé: paginação + contagem */}
      <div className="flex items-center justify-between gap-3 pt-2 text-[12px] text-gray-500 dark:text-gray-400">
        <span>{rows.length.toLocaleString('pt-BR')} linha(s) · {columns.length} colunas</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(Math.max(0, pageSafe - 1))} disabled={pageSafe === 0} className="p-1 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronLeft className="w-4 h-4" /></button>
            <span className="tabular-nums">Página {pageSafe + 1} de {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages - 1, pageSafe + 1))} disabled={pageSafe >= totalPages - 1} className="p-1 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpreadsheetGrid;
